"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { getThumbnailUrl, isVideoUrl, isYouTubeUrl } from "./utils";

const thumbnailsClasses =
  "flex gap-2 overflow-x-auto scroll-smooth [&::-webkit-scrollbar]:hidden w-full max-w-full";
const thumbnailClasses =
  "relative overflow-hidden border-2 transition-all duration-300 rounded-[var(--border-radius-x6,12px)] cursor-pointer focus:outline-none h-16 w-28 flex-shrink-0";

export interface MediaCarouselThumbnailsProps {
  urls: string[];
  selectedIndex: number;
  onSlideSelect: (index: number) => void;
  className?: string;
  fallbackThumbnailUrl?: string;
}

export const MediaCarouselThumbnails = ({
  urls,
  selectedIndex,
  onSlideSelect,
  className,
  fallbackThumbnailUrl,
}: MediaCarouselThumbnailsProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const checkOverflow = () => {
      const hasHorizontalOverflow =
        container.scrollWidth > container.clientWidth;
      setHasOverflow(hasHorizontalOverflow);
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    const selectedThumbnail = container?.children[selectedIndex] as HTMLElement;
    if (!container || !selectedThumbnail) return;

    const scrollLeft =
      selectedThumbnail.offsetLeft -
      (container.clientWidth - selectedThumbnail.offsetWidth) / 2;
    container.scrollTo({ left: Math.max(0, scrollLeft), behavior: "smooth" });
  }, [selectedIndex]);

  if (!urls || urls.length === 0) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        thumbnailsClasses,
        hasOverflow ? "justify-start pl-4 pr-4" : "justify-center",
        className,
      )}
    >
      {urls.map((url, index) => {
        const thumbnailUrl = getThumbnailUrl(url);
        const isActive = index === selectedIndex;
        const isHostedVideo = isVideoUrl(url) && !isYouTubeUrl(url);

        return (
          <button
            key={url}
            type="button"
            className={cn(
              thumbnailClasses,
              isActive
                ? "border-2 border-white scale-95 shadow-lg"
                : "border-transparent hover:border-black hover:scale-102",
            )}
            onClick={() => onSlideSelect(index)}
          >
            <Image
              src={
                isHostedVideo
                  ? fallbackThumbnailUrl || thumbnailUrl
                  : thumbnailUrl
              }
              alt={`Thumbnail ${index + 1}`}
              width={112}
              height={64}
              className="object-cover w-full h-full"
              sizes="112px"
              draggable={false}
            />
          </button>
        );
      })}
    </div>
  );
};
